/* ============================================================================
 * useWebSocketManager.ts - WebSocket connection and message handling
 * ============================================================================ */

import { useLayoutEffect, useEffect, useRef, useCallback, type MutableRefObject, type Dispatch, type SetStateAction } from 'react';
import type { ProjectData, NodeOperation } from '@/shared/types/projectData';
import type { SyncState, WebSocketState } from '@/shared/apiTypes';
import type { User } from '@/features/auth/services/authService';
import { ensureAuthRuntimeTokenStatus, getAuthRuntimeAccessToken } from '@/features/auth/services/authRuntime';
import { getAdminViewAsUserId } from '@/features/auth/services/adminViewAs';
import { webSocketSingleton } from '@/shared/api/WebSocketSingleton';
import { applyOperation, sanitizeProjectData } from '@/shared/utils/projectDataUtils';
import { transformNodeFromBackend, transformRealtimeOperationFromBackend } from '@/shared/utils/fieldNameTransform';
import { WS_URL } from '../constants';
import { useOperationQueue } from './useOperationQueue';
import { getProjectIdFromNodeId } from '@/features/projects/MultiProjectContext';
import { log } from '@/shared/utils/logger';
import { handleLoadMessage } from './handlers/loadMessageHandler';
import { handleOperationMessage } from './handlers/operationMessageHandler';
import { handleGroupMove } from './handlers/groupMoveHandler';
import { projectStatusAccessDetail } from './handlers/projectStatusAccess';
import { shouldSkipOperationResult, trackOperation as trackOpWithDedup } from './handlers/operationDeduplication';
import { normalizeOperationIds, checkIfNamespaced } from './handlers/nodeNormalization';
import { syncProjectCacheFromUpdatedData } from './utils/syncProjectCacheFromUpdatedData';
import { filterRealtimeProjectDataForUser } from '@/features/projects/helpers/realtimeVisibilityProjection';
import type { VisibilityTier } from '@/shared/types/projectData';
import { isOperationSupported } from '@/shared/utils/operations/handlers/operationRegistry';
import { getRemoteRefreshReason, operationResultTouchesVisibilityMutation } from './utils/remoteRefreshGate';
import { isWebSocketMsg } from '@/shared/api/handlers/messageHandlers';
import { isNodeOperation } from '@/shared/api/utils/queueManager';

/** User object shape needed by this hook */
type WsUser = User;

/** Shape of incoming WebSocket messages handled in onMessage */
interface WsMsg {
  type: string;
  data?: Record<string, unknown>;
  operation?: Record<string, unknown>;
  result?: Record<string, unknown> & { success?: boolean; updatedNodesFull?: unknown[]; node?: unknown; noMutation?: boolean };
  projectId?: string;
  project_id?: string;
  sessionId?: string;
  source?: string;
  error?: string;
  documentId?: string;
  changeType?: string;
  projectName?: string;
  client?: string;
  [key: string]: unknown;
}

const isWebSocketState = (value: unknown): value is WebSocketState =>
  value === 'disconnected' || value === 'connecting' || value === 'connected' || value === 'syncing';

const isSyncState = (value: unknown): value is SyncState =>
  value !== null && typeof value === 'object' &&
  typeof Reflect.get(value, 'localVersion') === 'number' &&
  typeof Reflect.get(value, 'serverVersion') === 'number' &&
  typeof Reflect.get(value, 'lastSyncTime') === 'number' &&
  Array.isArray(Reflect.get(value, 'pendingOperations')) &&
  typeof Reflect.get(value, 'syncStatus') === 'string';

interface UseWebSocketManagerParams {
  user: WsUser | null;
  projectData: ProjectData | null;
  activeProjectId?: string | null;
  visibleProjectIds?: string[];
  wsState: string;
  sessionId: MutableRefObject<string>;
  setProjectData: Dispatch<SetStateAction<ProjectData | null>>;
  setWsState: (state: WebSocketState) => void;
  setSyncState: (state: Partial<SyncState>) => void;
  setError: (error: string | null) => void;
  setExpandedNodeIds: (ids: Set<string>) => void;
  loadProjectDataFromServer: () => void;
  updateProjectInCache?: (projectId: string, updatedData: Partial<ProjectData>) => void;
  refreshProject?: (projectId: string) => Promise<void>;
  projectVisibilityDefaults?: Record<string, VisibilityTier | undefined>;
  projectOwnerIds?: Record<string, string | undefined>;
  /** False when the account may open no workspace view; no project socket is opened then. */
  realtimeEnabled?: boolean;
}

/**
 * Hook for WebSocket connection management
 */
export function useWebSocketManager({
  user,
  projectData,
  activeProjectId,
  visibleProjectIds = [],
  wsState: _wsState,
  sessionId,
  setProjectData,
  setWsState,
  setSyncState,
  setError,
  setExpandedNodeIds,
  loadProjectDataFromServer,
  updateProjectInCache,
  refreshProject,
  projectVisibilityDefaults = {},
  projectOwnerIds = {},
  realtimeEnabled = true,
}: UseWebSocketManagerParams) {
  const isInitialized = useRef(false);
  const processedOperations = useRef<Set<string>>(new Set());
  const recentProjectRefreshes = useRef<Map<string, number>>(new Map());
  const pendingProjectRefreshTimers = useRef<Map<string, number>>(new Map());
  const authRetryTimer = useRef<number | null>(null);
  const pendingConnectCancellation = useRef<(() => void) | null>(null);
  const projectVisibilityDefaultsRef = useRef(projectVisibilityDefaults);
  const projectOwnerIdsRef = useRef(projectOwnerIds);
  const projectVisibilityDefaultOverridesRef = useRef<Record<string, VisibilityTier | undefined>>({});
  const latestUserRef = useRef<WsUser | null>(user);
  // Singleton callbacks outlive renders and authorization-scoped loaders.
  // Forward through committed callbacks without reinitializing the connection.
  const refreshProjectRef = useRef(refreshProject);
  const loadProjectDataFromServerRef = useRef(loadProjectDataFromServer);
  useLayoutEffect(() => {
    refreshProjectRef.current = refreshProject;
    loadProjectDataFromServerRef.current = loadProjectDataFromServer;
  }, [refreshProject, loadProjectDataFromServer]);
  const primaryVisibleProjectId = visibleProjectIds.find((pid) => pid && typeof pid === 'string' && pid !== 'default') || null;
  const selectedProjectId = activeProjectId && activeProjectId !== 'default' ? activeProjectId : primaryVisibleProjectId;
  const isEffectiveViewAs = Boolean(getAdminViewAsUserId());
  const effectiveProjectId = realtimeEnabled
    ? selectedProjectId || (user?.role === 'admin' && !isEffectiveViewAs ? 'default' : null)
    : null;
  const realtimeProjectIds = realtimeEnabled ? [...visibleProjectIds, ...(activeProjectId ? [activeProjectId] : [])] : [];

  projectVisibilityDefaultsRef.current = projectVisibilityDefaults;
  projectOwnerIdsRef.current = projectOwnerIds;
  for (const [projectId, visibilityTierDefault] of Object.entries(projectVisibilityDefaultOverridesRef.current)) {
    if (projectVisibilityDefaults[projectId] === visibilityTierDefault) {
      delete projectVisibilityDefaultOverridesRef.current[projectId];
    }
  }

  const getRealtimeProjectVisibilityDefaults = useCallback(
    () => ({
      ...projectVisibilityDefaultsRef.current,
      ...projectVisibilityDefaultOverridesRef.current,
    }),
    []
  );

  // CRITICAL FIX: Initialize operation queue for sequential processing
  const { enqueueOperation, isQueueEmpty: _isQueueEmpty, getQueueLength: _getQueueLength } = useOperationQueue({
    setProjectData,
    projectData,
    user,
    updateProjectInCache,
    refreshProject,
    projectVisibilityDefaultsRef,
    projectVisibilityDefaults,
    projectOwnerIdsRef,
    projectOwnerIds,
  });
  const enqueueOperationRef = useRef(enqueueOperation);
  latestUserRef.current = user;
  enqueueOperationRef.current = enqueueOperation;
  // CRITICAL FIX: Track project ID separately to prevent reconnections on data changes
  const currentProjectIdRef = useRef<string | null>(effectiveProjectId);
  const connectionGenerationRef = useRef(0);
  const connectionUserIdRef = useRef<string | null>(user?.id ?? null);
  const latestProjectDataRef = useRef<ProjectData | null>(projectData);
  const latestProjectDataScopeRef = useRef<string>('');
  const latestPrincipalScopeRef = useRef<string>(`${user?.id || ''}|${user?.username || ''}|${user?.role || ''}`);
  const principalScope = `${user?.id || ''}|${user?.username || ''}|${user?.role || ''}`;
  const projectDataScope = `${effectiveProjectId}|${visibleProjectIds.join(',')}|${projectData?.projectId || ''}`;
  const hasLoadedProjectRows = Boolean(projectData && Array.isArray(projectData.nodes) && projectData.nodes.length > 0);
  if (principalScope !== latestPrincipalScopeRef.current) {
    latestPrincipalScopeRef.current = principalScope;
    latestProjectDataScopeRef.current = projectDataScope;
    latestProjectDataRef.current = null;
  } else if (projectDataScope !== latestProjectDataScopeRef.current) {
    latestProjectDataScopeRef.current = projectDataScope;
    latestProjectDataRef.current = projectData;
  } else if (hasLoadedProjectRows || !latestProjectDataRef.current) {
    latestProjectDataRef.current = projectData;
  }

  const markNodeDeleted = useCallback((projectId: string, nodeId: string) => {
    if (!projectId || !nodeId) return;

    // Simplified: only update local state, rely on server refresh for cache
    try {
      setProjectData((prev) => {
        if (!prev || !Array.isArray(prev.nodes)) return prev;
        const nodes = prev.nodes.map((n) =>
          n.id === nodeId ? { ...n, _deleted: true, isDeleted: true } : n
        );
        return { ...prev, nodes };
      });
    } catch { /* best-effort local state update */ }
  }, [setProjectData]);

  const clearAuthRetry = useCallback(() => {
    if (authRetryTimer.current !== null) {
      window.clearTimeout(authRetryTimer.current);
      authRetryTimer.current = null;
    }
  }, []);

  const scheduleAuthoritativeProjectRefresh = useCallback((projectId: string | null | undefined, reason: string, metadata?: Record<string, unknown>) => {
    if (!projectId || typeof refreshProjectRef.current !== 'function') {
      return;
    }

    const refreshOwner = webSocketSingleton.getPrincipalContext();
    const now = Date.now();
    const lastRefreshAt = recentProjectRefreshes.current.get(projectId) || 0;
    if (now - lastRefreshAt < 400) {
      if (pendingProjectRefreshTimers.current.has(projectId)) {
        return;
      }

      const trailingDelayMs = Math.max(450 - (now - lastRefreshAt), 75);
      const timeoutId = window.setTimeout(() => {
        pendingProjectRefreshTimers.current.delete(projectId);
        if (!webSocketSingleton.isCurrentPrincipal(refreshOwner)) return;
        recentProjectRefreshes.current.set(projectId, Date.now());
        log.debug('WebSocket', '🔄 [AUTHORITATIVE REFRESH]', {
          projectId,
          reason: `${reason}-coalesced`,
          ...metadata,
        });
        void refreshProjectRef.current?.(projectId).catch(() => {});
      }, trailingDelayMs);
      pendingProjectRefreshTimers.current.set(projectId, timeoutId);
      return;
    }

    const existingTimer = pendingProjectRefreshTimers.current.get(projectId);
    if (existingTimer !== undefined) {
      window.clearTimeout(existingTimer);
      pendingProjectRefreshTimers.current.delete(projectId);
    }
    recentProjectRefreshes.current.set(projectId, now);
    log.debug('WebSocket', '🔄 [AUTHORITATIVE REFRESH]', {
      projectId,
      reason,
      ...metadata,
    });
    void refreshProjectRef.current?.(projectId).catch(() => {});
  }, []);

  const connectSingletonWithValidAuth = useCallback(
    async function connectSingletonWithValidAuth(projectId: string, generation = connectionGenerationRef.current): Promise<void> {
      if (!user) return;

      pendingConnectCancellation.current?.();
      pendingConnectCancellation.current = null;
      clearAuthRetry();
      const owner = webSocketSingleton.getPrincipalContext();
      const tokenStatus = await ensureAuthRuntimeTokenStatus().catch(() => 'transient' as const);
      const hasRuntimeToken = Boolean(getAuthRuntimeAccessToken());

      if (!webSocketSingleton.isCurrentPrincipal(owner) || !user || generation !== connectionGenerationRef.current || currentProjectIdRef.current !== projectId) return;

      if (tokenStatus === 'valid' || (tokenStatus === 'transient' && hasRuntimeToken)) {
        if (tokenStatus === 'transient') {
          log.warn(
            'useWebSocketManager',
            'Auth runtime status is transient but an access token is present; connecting websocket to avoid unnecessary realtime disconnect churn'
          );
        }
        setError(null);
        pendingConnectCancellation.current = webSocketSingleton.connect(user.id, projectId) ?? null;
        return;
      }

      setWsState('disconnected');

      if (tokenStatus === 'transient') {
        setError('Authentication refresh temporarily unavailable for real-time sync');
        authRetryTimer.current = window.setTimeout(() => {
          authRetryTimer.current = null;
          void connectSingletonWithValidAuth(projectId, generation);
        }, 1500);
        return;
      }

      setError('Authentication required for real-time sync');
    },
    [clearAuthRetry, setError, setWsState, user]
  );

  // Retire the old principal in the commit before any passive auth wait or mutation.
  const principalUserId = user?.id;
  useLayoutEffect(() => {
    if (principalUserId) webSocketSingleton.activatePrincipal(principalUserId);
  }, [principalUserId]);

  // Initialize WebSocket singleton
  useEffect(() => {
    // Handle unauthenticated state explicitly
    if (!user) {
      connectionGenerationRef.current += 1;
      connectionUserIdRef.current = null;
      clearAuthRetry();
      webSocketSingleton.clearSession();
      isInitialized.current = false;
      setWsState('disconnected');
      setError('Authentication required for real-time sync');
      return;
    }
    
    // Initialize singleton only once
    if (!isInitialized.current) {
      log.info('useWebSocketManager', 'Initializing WebSocket singleton');

      try {
        (window as unknown as Record<string, unknown>).webSocketSingleton = webSocketSingleton;
      } catch { /* best-effort debug exposure */ }

      // Use the stable session ID from the singleton
      const stableSessionId = webSocketSingleton.getStableSessionId();
      sessionId.current = stableSessionId;

      webSocketSingleton.initialize({
        url: WS_URL,
        durableOperations: { transactionalReceiptsQualified: true },
        onStateChange: (state: unknown) => {
          log.info('useWebSocketManager', 'State changed to:', state);
          if (!isWebSocketState(state)) return;
          setWsState(state);
          // Clear error when connected
          if (state === 'connected') {
            // CRITICAL FIX: Update sessionId ref when WebSocket connects/reconnects
            const currentSessionId = webSocketSingleton.getStableSessionId();
            sessionId.current = currentSessionId;
            log.debug('useWebSocketManager', 'Updated sessionId ref to current session:', currentSessionId);
            // Signals sent while the socket was down (e.g. closed by an authority change) are lost; open views refetch.
            try {
              window.dispatchEvent(new CustomEvent('realtime-connected'));
            } catch { /* best-effort event dispatch */ }

            setError(null);
            const currentSyncState = webSocketSingleton.getManager()?.getSyncState();
            if (currentSyncState) setSyncState(currentSyncState);
            const connectedProjectId = currentProjectIdRef.current;
            if (connectedProjectId && connectedProjectId !== 'default') {
              scheduleAuthoritativeProjectRefresh(
                connectedProjectId,
                'websocket-connected-bootstrap',
                {
                  source: 'websocket-connected',
                  sessionId: currentSessionId,
                }
              );
            }
          }
        },
        onSyncStateChange: (state: unknown) => {
          if (isSyncState(state)) setSyncState(state);
        },
        onMessage: (rawMsg: unknown) => {
          if (!isWebSocketMsg(rawMsg)) {
            log.warn('useWebSocketManager', 'Ignored malformed WebSocket message');
            return;
          }
          const messageOwner = webSocketSingleton.getPrincipalContext();
          const message: WsMsg = rawMsg;
          if (!latestUserRef.current || webSocketSingleton.getPrincipalContext().userId !== latestUserRef.current.id) {
            log.debug('useWebSocketManager', 'Ignored WebSocket message without a current principal');
            return;
          }
          // Handle incoming WebSocket messages
          if (message.type === 'load' && message.data) {
            handleLoadMessage({ message, setProjectData, setExpandedNodeIds });
          } else if (message.type === 'operation' && message.operation) {
            handleOperationMessage({
              message,
              sessionId: sessionId.current,
              processedOperations: processedOperations.current,
              enqueueOperation: enqueueOperationRef.current
            });
            } else if (message.type === 'operation-result' && message.operation) {
            // Handle operation results from server broadcasts
            const rawOperation = message.operation as Record<string, unknown>;
            const nestedOp = rawOperation?.operation;
            const operation =
              nestedOp && typeof nestedOp === 'object'
                ? {
                    ...(nestedOp as Record<string, unknown>),
                    ...(rawOperation.id ? { id: rawOperation.id } : {}),
                    ...(rawOperation.nodeId ? { nodeId: rawOperation.nodeId } : {}),
                    ...(rawOperation.deletedNodeId ? { deletedNodeId: rawOperation.deletedNodeId } : {}),
                    ...(Array.isArray(rawOperation.deletedNodeIds) ? { deletedNodeIds: rawOperation.deletedNodeIds } : {}),
                    ...(rawOperation.parentId !== undefined ? { parentId: rawOperation.parentId } : {}),
                    ...(rawOperation.targetParentId !== undefined ? { targetParentId: rawOperation.targetParentId } : {}),
                    ...(rawOperation.sourceParentId !== undefined ? { sourceParentId: rawOperation.sourceParentId } : {}),
                    ...(Array.isArray(rawOperation.nodeIds) ? { nodeIds: rawOperation.nodeIds } : {}),
                    ...(Array.isArray(rawOperation.sourceNodeIds) ? { sourceNodeIds: rawOperation.sourceNodeIds } : {}),
                    ...(Array.isArray(rawOperation.updates) ? { updates: rawOperation.updates } : {}),
                    ...(Array.isArray(rawOperation.columnOrder) ? { columnOrder: rawOperation.columnOrder } : {}),
                    ...(rawOperation.projectId ? { projectId: rawOperation.projectId } : {}),
                    ...(rawOperation.sessionId ? { sessionId: rawOperation.sessionId } : {}),
                    ...(rawOperation.data && typeof rawOperation.data === 'object'
                      ? { data: rawOperation.data }
                      : {}),
                  }
                : rawOperation;
            const transformedOperation = transformRealtimeOperationFromBackend(operation);
            if (
              transformedOperation?.type === 'batch-update' &&
              !Array.isArray(transformedOperation?.updates) &&
              Array.isArray(transformedOperation?.data?.updates)
            ) {
              transformedOperation.updates = transformedOperation.data.updates;
            }
            const operationId =
              transformedOperation?.id ||
              (rawOperation?.id as string) ||
              `${(rawOperation?.type as string) || transformedOperation?.type}-${Date.now()}`;
            const operationSessionId = transformedOperation?.sessionId || (rawOperation?.sessionId as string | undefined);
            const messageSessionId = message.sessionId;
            const isGroupMove = transformedOperation && transformedOperation.type === 'group-move';
            const opRecord = message.operation as Record<string, unknown>;
            const isCanonical = Boolean(message.canonical || (opRecord && opRecord.canonical));
            const resultRecord = message.result as Record<string, unknown> | undefined;
            const hasUpdatedNodesFull = Array.isArray(resultRecord?.updatedNodesFull) && (resultRecord?.updatedNodesFull as unknown[]).length > 0;
            const hasAuthoritativeNode = Boolean(resultRecord?.node && typeof resultRecord.node === 'object');
            const targetProjectId =
              message.projectId ||
              (opRecord?.projectId as string | undefined) ||
              transformedOperation?.projectId ||
              (typeof transformedOperation?.nodeId === 'string'
                ? getProjectIdFromNodeId(transformedOperation.nodeId, currentProjectIdRef.current ?? undefined)
                : null) ||
              currentProjectIdRef.current;

            // Notify singleton waiters before dedup so same-session ack-only
            // results still resolve the optimistic mutation promise.
            try {
              const wsSingleton = (window as unknown as Record<string, unknown>).webSocketSingleton as
                | { notifyOperationResult?: (id: string, msg: unknown) => void }
                | undefined;
              wsSingleton?.notifyOperationResult?.(operationId, message);
            } catch { /* best-effort notification */ }

            // Check if we should skip this operation-result
            if (shouldSkipOperationResult({
              operationSessionId,
              messageSessionId,
              ourSessionId: sessionId.current,
              operationId,
              operation: transformedOperation,
              isGroupMove,
              isCanonical,
              hasUpdatedNodesFull,
              hasAuthoritativeNode,
              messageSource: message.source
            })) {
              return;
            }

            // Track the operation
            trackOpWithDedup(operationId, operationSessionId || sessionId.current, processedOperations.current);

            // Only process successful operations
            log.debug('WebSocket', '🔍 [SUCCESS CHECK]', {
              hasResult: !!message.result,
              success: message.result?.success,
              hasOperation: !!operation,
              willProcess: message.result?.success && operation,
              source: message.source
            });
            if (message.result?.success && transformedOperation) {
              const touchesVisibilityMutation = operationResultTouchesVisibilityMutation(
                transformedOperation as Record<string, unknown>,
                resultRecord
              );
              if (resultRecord?.noMutation) {
                log.debug('WebSocket', 'Skipping no-op operation-result repaint', {
                  operationType: transformedOperation.type,
                  operationId,
                });
                return;
              }

              // CRITICAL FIX: MCP operations should use the operation queue for sequential processing
              // This prevents race conditions when operations arrive rapidly
              if (message.source === 'mcp') {
                let queuedOperation = transformedOperation;
                const authoritativeUpdatedNodes = Array.isArray(resultRecord?.updatedNodesFull)
                  ? (resultRecord.updatedNodesFull as unknown[]).map((node: unknown) => transformNodeFromBackend(node))
                  : [];
                const shouldPreserveStructuralOperation =
                  transformedOperation.type === 'move' ||
                  transformedOperation.type === 'reorder' ||
                  transformedOperation.type === 'group-move' ||
                  transformedOperation.type === 'delete' ||
                  transformedOperation.type === 'delete-tree' ||
                  transformedOperation.type === 'batch-delete';
                if (authoritativeUpdatedNodes.length > 0) {
                  const normalizedUpdatedNodes = authoritativeUpdatedNodes
                    .filter((node: unknown): node is Record<string, unknown> => node != null && typeof node === 'object' && typeof (node as Record<string, unknown>).id === 'string')
                    .map((node) => ({ nodeId: (node as unknown as Record<string, string>).id, node }));
                  queuedOperation = {
                    ...transformedOperation,
                    ...(shouldPreserveStructuralOperation ? {} : { type: 'batch-update' }),
                    updatedNodes: normalizedUpdatedNodes,
                    updatedNodesFull: authoritativeUpdatedNodes,
                  } as typeof transformedOperation;
                } else {
                  const operationProjectId =
                    (opRecord?.projectId as string | undefined) ||
                    message.projectId ||
                    transformedOperation?.projectId ||
                    (typeof transformedOperation?.nodeId === 'string'
                      ? getProjectIdFromNodeId(transformedOperation.nodeId, currentProjectIdRef.current ?? undefined)
                      : undefined) ||
                    currentProjectIdRef.current;
                  const authoritativeNode = transformNodeFromBackend(resultRecord?.node);
                  const shouldUseAuthoritativeResultNode =
                    authoritativeNode &&
                    typeof authoritativeNode === 'object' &&
                    (transformedOperation.type === 'create' || transformedOperation.type === 'update');
                  if (shouldUseAuthoritativeResultNode) {
                    queuedOperation = {
                      ...transformedOperation,
                      nodeId: authoritativeNode.id || transformedOperation.nodeId,
                      parentId: authoritativeNode.parentId ?? transformedOperation.parentId,
                      targetIndex:
                        typeof authoritativeNode.order === 'number'
                          ? authoritativeNode.order
                          : transformedOperation.targetIndex,
                      data: {
                        ...transformedOperation.data,
                        projectId: transformedOperation.data?.projectId ?? operationProjectId,
                        ...authoritativeNode,
                      },
                    };
                  }
                }
                log.debug('WebSocket', '🔄 MCP OPERATION-RESULT: Using queue for sequential processing', {
                  type: transformedOperation.type,
                  nodeId: transformedOperation.nodeId,
                  operationId
                });
                enqueueOperationRef.current(queuedOperation, 'operation-result', message);
                const isSessionlessMcpResult =
                  !operationSessionId &&
                  !messageSessionId;
                const shouldRefreshSessionlessBatchUpdate =
                  isSessionlessMcpResult &&
                  queuedOperation.type === 'batch-update';
                if (touchesVisibilityMutation) {
                  scheduleAuthoritativeProjectRefresh(
                    targetProjectId,
                    'mcp-visibility-mutation',
                    {
                      operationId,
                      operationType: queuedOperation.type,
                      source: message.source,
                    }
                  );
                } else if (shouldRefreshSessionlessBatchUpdate) {
                  scheduleAuthoritativeProjectRefresh(
                    targetProjectId,
                    'sessionless-mcp-batch-update',
                    {
                      operationId,
                      operationType: queuedOperation.type,
                      source: message.source,
                    }
                  );
                }
                return; // Exit early - operation will be processed by queue
              }

              // Store updated project data for cache update AFTER setProjectData
              // This prevents race conditions from nested state updates while keeping
              // project cache writes scoped to the actual target project.
              let updatedDataForCache: ProjectData | null = null;
              let cacheOperation: NodeOperation = transformedOperation;

              const currentData = latestProjectDataRef.current;
              log.debug('WebSocket', '🔍 [REMOTE APPLY] Preparing watcher state update', {
                hasCurrentData: !!currentData,
                currentNodesCount: currentData?.nodes?.length
              });

              if (currentData) {
                // Backend MUST send updatedNodesFull in message.result - no fallback
                const updatedNodesFull = resultRecord?.updatedNodesFull;
                const transformedUpdatedNodesFull = Array.isArray(updatedNodesFull)
                  ? (updatedNodesFull as unknown[]).map((node: unknown) => transformNodeFromBackend(node))
                  : [];
                const hasUpdatedNodes = transformedUpdatedNodesFull.length > 0;

                log.debug('WebSocket', '🔍 [REMOTE APPLY] Checking group-move conditions', {
                  isGroupMove,
                  hasUpdatedNodes,
                  updatedNodesCount: Array.isArray(updatedNodesFull) ? updatedNodesFull.length : 0
                });

                if (isGroupMove && hasUpdatedNodes) {
                  // Extract project ID for group-move
                  let operationProjectId = (opRecord?.projectId as string | undefined) || message.projectId;

                  // FALLBACK: Extract from namespaced nodeId if project_id missing
                  const opNodeId = opRecord?.nodeId;
                  if (!operationProjectId && opNodeId && typeof opNodeId === 'string' && opNodeId.includes(':')) {
                    operationProjectId = getProjectIdFromNodeId(opNodeId);
                    log.debug('WebSocket', '🔍 [GROUP-MOVE] Extracted project_id from nodeId:', {
                      nodeId: opNodeId,
                      extractedProjectId: operationProjectId
                    });
                  }

                  // Use handler for complex group-move logic
                  const updatedData = handleGroupMove({
                    message,
                    currentData,
                    operationProjectId: operationProjectId || currentData.projectId,
                    isGroupMove
                  });

                  const sanitizedUpdatedData = sanitizeProjectData(updatedData);
                  const visibilityProjectedData = filterRealtimeProjectDataForUser(
                    sanitizedUpdatedData,
                    latestUserRef.current,
                    getRealtimeProjectVisibilityDefaults(),
                    projectOwnerIdsRef.current
                  );
                  updatedDataForCache = visibilityProjectedData;
                  latestProjectDataRef.current = visibilityProjectedData;
                  setProjectData(visibilityProjectedData);

                  log.debug('WebSocket', '🔍 [GROUP-MOVE] Returning updated data with', visibilityProjectedData.nodes.length, 'nodes');
                } else {
                  // Default: apply the operation using the standard applyOperation function
                  const idsAreNamespaced = checkIfNamespaced(currentData.nodes);
                  const operationProjectId =
                    (opRecord?.projectId as string | undefined) ||
                    message.projectId ||
                    transformedOperation?.projectId ||
                    (typeof transformedOperation?.nodeId === 'string'
                      ? getProjectIdFromNodeId(transformedOperation.nodeId, currentData.projectId)
                      : undefined) ||
                    currentData.projectId;

                  let normalizedOp = normalizeOperationIds(
                    transformedOperation,
                    idsAreNamespaced,
                    operationProjectId
                  );

                  const authoritativeUpdatedNodes = transformedUpdatedNodesFull;
                  const shouldPreserveStructuralOperation =
                    normalizedOp.type === 'move' ||
                    normalizedOp.type === 'reorder' ||
                    normalizedOp.type === 'group-move' ||
                    normalizedOp.type === 'delete' ||
                    normalizedOp.type === 'delete-tree' ||
                    normalizedOp.type === 'batch-delete';
                  if (authoritativeUpdatedNodes.length > 0) {
                    normalizedOp = normalizeOperationIds(
                      {
                        ...normalizedOp,
                        ...(shouldPreserveStructuralOperation ? {} : { type: 'batch-update' }),
                        updatedNodes: authoritativeUpdatedNodes
                          .filter((node: unknown): node is Record<string, unknown> => node != null && typeof node === 'object' && typeof (node as Record<string, unknown>).id === 'string')
                          .map((node) => ({ nodeId: (node as unknown as Record<string, string>).id, node })),
                        updatedNodesFull: authoritativeUpdatedNodes,
                      } as typeof normalizedOp,
                      idsAreNamespaced,
                      operationProjectId
                    );
                  }

                  const authoritativeNode = transformNodeFromBackend(resultRecord?.node);
                  if (authoritativeUpdatedNodes.length === 0 && authoritativeNode && typeof authoritativeNode === 'object') {
                    normalizedOp = {
                      ...normalizedOp,
                      nodeId: authoritativeNode.id || normalizedOp.nodeId,
                      parentId: authoritativeNode.parentId ?? normalizedOp.parentId,
                      targetIndex:
                        typeof authoritativeNode.order === 'number'
                          ? authoritativeNode.order
                          : normalizedOp.targetIndex,
                      data: {
                        ...((normalizedOp.data as Record<string, unknown>) || {}),
                        projectId: (normalizedOp.data as Record<string, unknown>)?.projectId ?? operationProjectId,
                        ...authoritativeNode,
                      },
                    };
                    normalizedOp = normalizeOperationIds(
                      normalizedOp,
                      idsAreNamespaced,
                      operationProjectId
                    );
                  }

                  if (!isNodeOperation(normalizedOp)) {
                    log.warn('useWebSocketManager', 'Ignored unsupported normalized operation', { type: normalizedOp.type });
                    return currentData;
                  }
                  const updatedData = sanitizeProjectData(applyOperation(currentData, normalizedOp));
                  const visibilityProjectedData = filterRealtimeProjectDataForUser(
                    updatedData,
                    latestUserRef.current,
                    getRealtimeProjectVisibilityDefaults(),
                    projectOwnerIdsRef.current
                  );

                  updatedDataForCache = visibilityProjectedData;
                  cacheOperation = normalizedOp;
                  latestProjectDataRef.current = visibilityProjectedData;
                  setProjectData(visibilityProjectedData);
                }

                log.debug('WebSocket', '🔍 [CACHE UPDATE CHECK]', {
                  hasUpdatedDataForCache: !!updatedDataForCache,
                  updatedNodesLength: updatedDataForCache?.nodes?.length,
                  hasUpdateProjectInCache: !!updateProjectInCache
                });

                if (updatedDataForCache && updateProjectInCache) {
                  log.debug('WebSocket', '🔍 [CACHE UPDATE] Updating cache after watcher patch');
                  const cachedData = updatedDataForCache;
                  const syncCache = () => {
                    if (!webSocketSingleton.isCurrentPrincipal(messageOwner)) return;
                    syncProjectCacheFromUpdatedData({
                      updatedData: cachedData,
                      operation: cacheOperation,
                      message,
                      updateProjectInCache,
                      refreshProject: refreshProjectRef.current,
                    });
                    log.debug('WebSocket', '🔍 [CACHE UPDATE] Cache update completed');
                  };
                  if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
                    window.requestAnimationFrame(syncCache);
                  } else {
                    syncCache();
                  }
                } else {
                  log.debug('WebSocket', '❌ [CACHE UPDATE] Skipping cache update - missing required data');
                }
              }

              const isRemoteSession =
                Boolean(operationSessionId && operationSessionId !== sessionId.current) ||
                Boolean(messageSessionId && messageSessionId !== sessionId.current);
              const operationType = typeof transformedOperation.type === 'string'
                ? transformedOperation.type
                : '';
              const remoteRefreshReason = getRemoteRefreshReason({
                operation: transformedOperation as Record<string, unknown>,
                resultRecord,
                isRemoteSession,
                refreshProjectAvailable: typeof refreshProjectRef.current === 'function',
                touchesVisibilityMutation,
                operationSupported: !!operationType && isOperationSupported(operationType),
              });

              if (remoteRefreshReason && targetProjectId) {
                scheduleAuthoritativeProjectRefresh(
                  targetProjectId,
                  remoteRefreshReason,
                  {
                    operationType: transformedOperation.type,
                    operationId,
                    source: message.source,
                  }
                );
              }
            }
          } else if (message.type === 'operation-error') {
            const errorText = String(message.error || "");
            if (errorText.includes('not found in project')) {
              // Backend guarantees camelCase in WebSocket messages (converted via convert_websocket_message)
              const errOpRecord = message.operation as Record<string, unknown> | undefined;
              const pid = message.projectId || (errOpRecord?.projectId as string | undefined);
              const nid = errOpRecord?.nodeId as string | undefined;
              if (pid) {
                const multiCtx = (window as unknown as Record<string, unknown>).__multiProjectContext as
                  | { reloadProject?: (id: string) => void }
                  | undefined;
                if (multiCtx?.reloadProject) {
                  multiCtx.reloadProject(pid);
                }
                if (nid) {
                  markNodeDeleted(pid, nid);
                }
              }
            }
          } else if (message.type === 'import-complete') {
            const targetProjectId = message.projectId || message.project_id;
            if (targetProjectId && refreshProjectRef.current) {
              void refreshProjectRef.current(targetProjectId).catch(() => {});
            } else {
              loadProjectDataFromServerRef.current();
            }
          } else if (message.type === 'project_updated') {
            const targetProjectId = message.projectId || message.project_id;
            if (targetProjectId) {
              const requestedVisibilityTier =
                (typeof message.visibilityTierDefault === 'string' && message.visibilityTierDefault) ||
                (typeof message.visibility_tier_default === 'string' && message.visibility_tier_default) ||
                undefined;
              const visibilityTierDefault: VisibilityTier | undefined =
                requestedVisibilityTier === 'internal' || requestedVisibilityTier === 'partner' || requestedVisibilityTier === 'client'
                  ? requestedVisibilityTier
                  : undefined;
              const payload: Partial<ProjectData> = {};
              if (typeof message.projectName === 'string') payload.projectName = message.projectName;
              if (Object.prototype.hasOwnProperty.call(message, 'description')) payload.description = (message.description as string | undefined) ?? undefined;
              if (Object.prototype.hasOwnProperty.call(message, 'client')) payload.client = (message.client as string | undefined) ?? undefined;
              if (Object.prototype.hasOwnProperty.call(message, 'companyName')) payload.companyName = (message.companyName as string | undefined) ?? undefined;
              if (Object.prototype.hasOwnProperty.call(message, 'status')) payload.status = message.status as ProjectData['status'];
              if (Object.prototype.hasOwnProperty.call(message, 'githubConfig')) payload.githubConfig = message.githubConfig as ProjectData['githubConfig'];
              if (Object.prototype.hasOwnProperty.call(message, 'projectSlug')) payload.projectSlug = message.projectSlug as string | undefined;
              const hasProjectType = Object.prototype.hasOwnProperty.call(message, 'projectType');
              const hasWorkflowStatus = Object.prototype.hasOwnProperty.call(message, 'workflowStatus');
              const hasLeadAgentProfileId = Object.prototype.hasOwnProperty.call(message, 'leadAgentProfileId');
              if (visibilityTierDefault) {
                projectVisibilityDefaultOverridesRef.current[targetProjectId] = visibilityTierDefault as VisibilityTier;
                payload.metadata = {
                  settings: {
                    visibilityTierDefault,
                  },
                } as ProjectData['metadata'];
                (payload as Partial<ProjectData> & { visibilityTierDefault?: string }).visibilityTierDefault = visibilityTierDefault;
              }
              if (hasProjectType || hasWorkflowStatus || hasLeadAgentProfileId) {
                payload.metadata = {
                  ...(payload.metadata || {}),
                  settings: {
                    ...(payload.metadata?.settings || {}),
                    ...(hasProjectType ? { projectType: (message.projectType as ProjectData['metadata']['settings']['projectType']) ?? null } : {}),
                    ...(hasWorkflowStatus ? { workflowStatus: message.workflowStatus as ProjectData['metadata']['settings']['workflowStatus'] } : {}),
                    ...(hasLeadAgentProfileId ? { leadAgentProfileId: (message.leadAgentProfileId as string | null | undefined) ?? null } : {}),
                  },
                } as ProjectData['metadata'];
                if (hasLeadAgentProfileId) {
                  (payload as Partial<ProjectData>).leadAgentProfileId = (message.leadAgentProfileId as string | null | undefined) ?? null;
                }
              }

              if (updateProjectInCache && Object.keys(payload).length > 0) {
                updateProjectInCache(targetProjectId, payload);
              }

              // Another member archived or restored this project: drop it from (or re-offer it to) the open views.
              const statusAccess = projectStatusAccessDetail(targetProjectId, message as Record<string, unknown>);
              if (statusAccess) {
                window.dispatchEvent(new CustomEvent('project_access_updated', { detail: statusAccess }));
              }

              const effectiveActiveProjectId = currentProjectIdRef.current || projectData?.projectId || activeProjectId || primaryVisibleProjectId;
              if (effectiveActiveProjectId === targetProjectId && Object.keys(payload).length > 0) {
                setProjectData((currentData) => {
                  if (!currentData || currentData.projectId !== targetProjectId) {
                    return currentData;
                  }

                  const hasMetadataPatch = Boolean(visibilityTierDefault) || hasProjectType || hasWorkflowStatus || hasLeadAgentProfileId;
                  const currentSettings = currentData.metadata.settings;
                  const nextMetadata: ProjectData['metadata'] = hasMetadataPatch
                    ? {
                        ...currentData.metadata,
                        settings: {
                          ...currentSettings,
                          ...(visibilityTierDefault ? { visibilityTierDefault } : {}),
                          ...(hasProjectType ? { projectType: (message.projectType as ProjectData['metadata']['settings']['projectType']) ?? null } : {}),
                          ...(hasWorkflowStatus ? { workflowStatus: message.workflowStatus as ProjectData['metadata']['settings']['workflowStatus'] } : {}),
                          ...(hasLeadAgentProfileId ? { leadAgentProfileId: (message.leadAgentProfileId as string | null | undefined) ?? null } : {}),
                        },
                      }
                    : currentData.metadata;

                  return {
                    ...currentData,
                    ...(payload.projectName !== undefined ? { projectName: payload.projectName } : {}),
                    ...(Object.prototype.hasOwnProperty.call(payload, 'description') ? { description: payload.description } : {}),
                    ...(Object.prototype.hasOwnProperty.call(payload, 'client') ? { client: payload.client } : {}),
                    ...(Object.prototype.hasOwnProperty.call(payload, 'companyName') ? { companyName: payload.companyName } : {}),
                    ...(Object.prototype.hasOwnProperty.call(payload, 'status') ? { status: payload.status } : {}),
                    ...(Object.prototype.hasOwnProperty.call(payload, 'githubConfig') ? { githubConfig: payload.githubConfig } : {}),
                    ...(Object.prototype.hasOwnProperty.call(payload, 'projectSlug') ? { projectSlug: payload.projectSlug } : {}),
                    ...(hasLeadAgentProfileId ? { leadAgentProfileId: (message.leadAgentProfileId as string | null | undefined) ?? null } : {}),
                    metadata: nextMetadata,
                  };
                });
              }

              if (visibilityTierDefault) {
                scheduleAuthoritativeProjectRefresh(targetProjectId, 'project-visibility-default-updated', {
                  visibilityTierDefault,
                  source: message.source,
                });
              }
            }
          } else if (message.type === 'auth-updated') {
            try {
              window.dispatchEvent(new CustomEvent('auth-updated', { detail: message }));
            } catch { /* best-effort event dispatch */ }
          } else if (message.type === 'auth-invalidated') {
            try {
              window.dispatchEvent(new CustomEvent('auth-invalidated', { detail: message }));
            } catch { /* best-effort event dispatch */ }
          } else if (message.type === 'external-change') {
            // App writes are reconciled by operation results. A frame explicitly
            // identified as a direct database change has no such payload and needs
            // an authoritative scoped refresh.
            const affectedProjectId = message.projectId || message.project_id || currentProjectIdRef.current;
            log.info('useWebSocketManager', 'External change detected:', {
              changeType: message.changeType,
              projectId: affectedProjectId,
              source: message.source
            });
            if (message.source === 'mongodb-direct') {
              scheduleAuthoritativeProjectRefresh(affectedProjectId, 'external-database-change', {
                changeType: message.changeType,
                source: message.source,
              });
            }
          }
          else if (message.type === 'project_access_updated') {
            try {
              window.dispatchEvent(new CustomEvent('project_access_updated', { detail: message }));
              window.dispatchEvent(new CustomEvent('auth-updated', { detail: message }));
            } catch { /* best-effort event dispatch */ }
          } else if (message.type === 'mention-notification') {
            try {
              window.dispatchEvent(new CustomEvent('mention-notification', { detail: message }));
            } catch { /* best-effort event dispatch */ }
          } else if (message.type === 'inbox-notification') {
            try {
              window.dispatchEvent(new CustomEvent('inbox-notification', { detail: message }));
            } catch { /* best-effort event dispatch */ }
          } else if (message.type === 'organization-authority-updated') {
            // Open Settings authority views refetch through their own read gate; the signal carries no data.
            try {
              window.dispatchEvent(new CustomEvent('organization-authority-updated', { detail: message }));
            } catch { /* best-effort event dispatch */ }
          }
        },
        onError: (error: unknown) => {
          const errMsg = error instanceof Error ? error.message : String(error);
          log.error('useWebSocketManager', 'WebSocket error:', errMsg);
          setError(errMsg);
        }
      });
      
      isInitialized.current = true;
    }
    
    const owner = webSocketSingleton.activatePrincipal(user.id);
    webSocketSingleton.getManager()?.reconcileSubscriptions(realtimeProjectIds, owner);

  // Connect with the current project (initial)
  const projectId = effectiveProjectId;
    if (connectionUserIdRef.current !== user.id) {
      connectionGenerationRef.current += 1;
      connectionUserIdRef.current = user.id;
    }
    if (!projectId) {
      currentProjectIdRef.current = null;
      connectionGenerationRef.current += 1;
      clearAuthRetry();
      webSocketSingleton.disconnect();
      setWsState('disconnected');
      setError(null);
      return;
    }
    if (currentProjectIdRef.current !== projectId) {
      connectionGenerationRef.current += 1;
      currentProjectIdRef.current = projectId;
    }
    log.info('useWebSocketManager', 'Connecting singleton with:', {
      sessionId: webSocketSingleton.getStableSessionId(),
      userId: user.id,
      projectId
    });

    // Connect using singleton only after auth runtime has a valid access token.
    void connectSingletonWithValidAuth(projectId);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- Initial bootstrap should happen once per authenticated user session
  }, [user?.id, connectSingletonWithValidAuth, clearAuthRetry]);

  // CRITICAL FIX: Separate effect for project switching - prevents reconnection on data changes
  useEffect(() => {
    if (!user) return;

    const owner = webSocketSingleton.activatePrincipal(user.id);
    webSocketSingleton.getManager()?.reconcileSubscriptions(realtimeProjectIds, owner);
    const newProjectId = effectiveProjectId;

    if (!newProjectId) {
      if (currentProjectIdRef.current !== null) {
        connectionGenerationRef.current += 1;
        currentProjectIdRef.current = null;
        clearAuthRetry();
        webSocketSingleton.disconnect();
        setWsState('disconnected');
        setError(null);
      }
      return;
    }

    // Only switch project if it actually changed
    if (currentProjectIdRef.current !== newProjectId) {
      log.info('useWebSocketManager', 'Project ID changed from', currentProjectIdRef.current, 'to', newProjectId);
      connectionGenerationRef.current += 1;
      currentProjectIdRef.current = newProjectId;

      // If already connected, just switch project subscription
      if (webSocketSingleton.isConnected()) {
        log.info('useWebSocketManager', 'Switching project subscription without reconnecting');
        webSocketSingleton.switchProject(newProjectId);
        if (newProjectId && newProjectId !== 'default') {
          scheduleAuthoritativeProjectRefresh(newProjectId, 'project-switch-bootstrap', {
            source: 'project-switch',
          });
        }
      } else {
        // If not connected yet, connect with the new project
        log.debug('useWebSocketManager', 'Not connected yet, will connect with project:', newProjectId);
        void connectSingletonWithValidAuth(newProjectId);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- user?.id covers the user dependency; adding user object would cause excess re-runs
  }, [user?.id, effectiveProjectId, realtimeEnabled, visibleProjectIds, activeProjectId, connectSingletonWithValidAuth, clearAuthRetry, setError, setWsState]);

  // Record the complete desired set with principal activation, before auth.
  // Manager reconciliation is idempotent; ProjectDataContext owns refreshes.

  // Cleanup only local auth retry state on unmount. The singleton owns its
  // own lifecycle and should survive route-level hook churn.
  useEffect(() => {
    const timers = pendingProjectRefreshTimers.current;
    return () => {
      connectionGenerationRef.current += 1;
      pendingConnectCancellation.current?.();
      pendingConnectCancellation.current = null;
      clearAuthRetry();
      for (const timeoutId of timers.values()) {
        window.clearTimeout(timeoutId);
      }
      timers.clear();
    };
  }, [clearAuthRetry]);

  return {
    wsManager: { current: webSocketSingleton.getManager() },
    connectionProjectId: effectiveProjectId,
    processedOperations
  };
}
